/* eslint-disable react/require-default-props */
/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FaTrash } from 'react-icons/fa';

import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
   SectionActions,
} from '../../../../styles/layout/Section';
import { TableWrapper } from '../../../../styles/table/TableWrapper';
import { Table } from '../../../../styles/table/Table';
import { EmptyState } from '../../../../styles/feedback/EmptyState';
import { IconButton } from '../../../../styles/ui/Button';

import NewCommissionModal from './NewCommissionModal';
import CommissionSuggestionModal from './CommissionSuggestionModal';
import {
   listCommissionEntries,
   deleteCommissionEntry,
} from '../../../../services/routing/routingApi';

// =============================================================================
// Painel 4 — Comissões.
//
// Visível somente quando item.item_status === 'REALIZADO' (regra do backend).
// Para outros status mostra um aviso explicativo.
//
// CRUD básico (escopo desta sessão):
//   ✓ listar   — canCreate (routing:commission_entry:create OU routing:finance:operate)
//   ✓ criar    — canCreate (idem)
//   ✓ remover  — canRemove (routing:commission_entry:delete; SÓ logística — M3 §2)
//   ✗ editar   — vira Bloco D futuro
//
// M3 (§2): o perfil financeiro (routing:finance:operate) lança e lista, mas NÃO
// edita/remove. Por isso create/list e remove vêm em props separadas.
// =============================================================================

const HeaderActions = styled.div`
   display: flex;
   gap: 8px;
   align-items: center;
`;

const TotalBadge = styled.span`
   display: inline-flex;
   align-items: center;
   padding: 4px 10px;
   border-radius: 999px;
   font-size: 0.75rem;
   font-weight: 600;
   background: rgba(46, 125, 50, 0.12);
   color: #1b5e20;
`;

const NotEligibleBox = styled.div`
   background: rgba(0, 0, 0, 0.04);
   border: 1px solid rgba(0, 0, 0, 0.08);
   border-radius: 8px;
   padding: 12px 14px;
   font-size: 0.8rem;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   line-height: 1.5;
`;

const RemoveButton = styled(IconButton).attrs({ $intent: 'danger' })`
   width: 28px;
   height: 28px;
   color: ${({ theme }) => theme.colors.danger};
`;

function formatCurrency(value) {
   if (value === null || value === undefined || value === '') return 'R$ 0,00';
   const numeric = Number(value);
   if (Number.isNaN(numeric)) return 'R$ 0,00';
   return numeric.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
   });
}

// =============================================================================

export default function ItemCommissionsPanel({
   item,
   canCreate,
   canRemove,
   onEntriesChange,
}) {
   const [entries, setEntries] = useState([]);
   const [isLoading, setIsLoading] = useState(false);
   const [isModalOpen, setIsModalOpen] = useState(false);
   const [isSuggestionOpen, setIsSuggestionOpen] = useState(false);
   const [removingId, setRemovingId] = useState(null);

   const isRealized = item?.item_status === 'REALIZADO';

   const fetchEntries = useCallback(async () => {
      if (!item?.id) return;
      setIsLoading(true);
      try {
         const list = await listCommissionEntries(item.id);
         setEntries(list);
         // Reporta a contagem pro pai (RoutingItemShow) — alimenta a preempção
         // da trava de comissão (commission_locked) no painel de edição. M3 §3.
         onEntriesChange?.(list.length);
      } catch (err) {
         const apiMessage =
            err?.response?.data?.message ??
            err?.message ??
            'Erro ao carregar lançamentos de comissão.';
         toast.error(apiMessage);
         setEntries([]);
         onEntriesChange?.(0);
      } finally {
         setIsLoading(false);
      }
   }, [item?.id, onEntriesChange]);

   // Carrega lançamentos quando o item ou o status muda.
   // Mesmo se não está REALIZADO, ainda mostra os lançamentos órfãos preservados.
   useEffect(() => {
      fetchEntries();
   }, [fetchEntries, item?.item_status]);

   // ─── Handlers ──────────────────────────────────────────────
   const handleOpenModal = () => setIsModalOpen(true);
   const handleCloseModal = () => setIsModalOpen(false);

   const handleCommissionCreated = () => {
      // Refetch garante consistência (total_value calculado pelo backend, etc.)
      fetchEntries();
   };

   const handleRemove = async (entry) => {
      if (!entry?.id || removingId) return;

      const confirmed = window.confirm(
         `Remover o lançamento "${entry.service_key} — ${entry.service_description}"?\n\nEsta ação não pode ser desfeita.`
      );
      if (!confirmed) return;

      setRemovingId(entry.id);
      try {
         const result = await deleteCommissionEntry(item.id, entry.id);
         toast.success(result.message || 'Lançamento removido.');
         fetchEntries();
      } catch (err) {
         const apiMessage =
            err?.response?.data?.message ??
            err?.message ??
            'Erro ao remover lançamento.';
         toast.error(apiMessage);
      } finally {
         setRemovingId(null);
      }
   };

   // ─── Total acumulado dos lançamentos ───────────────────────
   const totalSum = entries.reduce(
      (acc, entry) => acc + (Number(entry.total_value) || 0),
      0
   );

   if (!item) return null;

   // ─── Render ─────────────────────────────────────────────────
   return (
      <>
         <Section>
            <SectionHeader>
               <SectionTitle>Comissões</SectionTitle>

               <SectionActions>
                  <HeaderActions>
                     {entries.length > 0 && (
                        <TotalBadge>
                           Total: {formatCurrency(totalSum)}
                        </TotalBadge>
                     )}
                     {isRealized && canCreate && (
                        <button
                           type="button"
                           onClick={() => setIsSuggestionOpen(true)}
                           disabled={isLoading}
                        >
                           Sugerir da seleção
                        </button>
                     )}
                     {isRealized && canCreate && (
                        <button
                           type="button"
                           onClick={handleOpenModal}
                           disabled={isLoading}
                        >
                           + Novo lançamento
                        </button>
                     )}
                  </HeaderActions>
               </SectionActions>
            </SectionHeader>

            <SectionBody>
               {!isRealized && (
                  <NotEligibleBox style={{ marginBottom: entries.length > 0 ? 12 : 0 }}>
                     {entries.length > 0 ? (
                        <>
                           Estes lançamentos foram registrados quando o item estava em{' '}
                           <strong>Realizado</strong> e estão{' '}
                           <strong>preservados mas bloqueados</strong>. Para criar novos,
                           volte o status para <strong>Realizado</strong> no painel acima.
                        </>
                     ) : (
                        <>
                           Lançamentos de comissão só podem ser criados quando o item
                           está com status <strong>Realizado</strong>. Mude o status no
                           painel acima para começar.
                        </>
                     )}
                  </NotEligibleBox>
               )}

               {isLoading && <p style={{ opacity: 0.7 }}>Carregando lançamentos…</p>}

               {!isLoading && entries.length === 0 && isRealized && (
                  <EmptyState
                     title="Nenhum lançamento de comissão"
                     description='Clique em "Novo lançamento" para registrar.'
                  />
               )}

               {!isLoading && entries.length > 0 && (
                  <TableWrapper $minWidth="640px">
                     <Table>
                        <thead>
                           <tr>
                              <th style={{ textAlign: 'left' }}>Código</th>
                              <th style={{ textAlign: 'left' }}>Descrição</th>
                              <th style={{ textAlign: 'right', width: 70 }}>Qtd</th>
                              <th style={{ textAlign: 'right', width: 110 }}>
                                 Unitário
                              </th>
                              <th style={{ textAlign: 'right', width: 110 }}>
                                 Total
                              </th>
                              {isRealized && canRemove && (
                                 <th style={{ textAlign: 'center', width: 60 }}>
                                    {' '}
                                 </th>
                              )}
                           </tr>
                        </thead>
                        <tbody>
                           {entries.map((entry) => (
                              <tr key={entry.id}>
                                 <td>{entry.service_key}</td>
                                 <td>{entry.service_description}</td>
                                 <td style={{ textAlign: 'right' }}>
                                    {entry.quantity}
                                 </td>
                                 <td style={{ textAlign: 'right' }}>
                                    {formatCurrency(entry.unit_value)}
                                 </td>
                                 <td style={{ textAlign: 'right', fontWeight: 600 }}>
                                    {formatCurrency(entry.total_value)}
                                 </td>
                                 {isRealized && canRemove && (
                                    <td style={{ textAlign: 'center' }}>
                                       <RemoveButton
                                          type="button"
                                          onClick={() => handleRemove(entry)}
                                          disabled={removingId === entry.id}
                                          title="Remover lançamento"
                                       >
                                          <FaTrash size={12} />
                                       </RemoveButton>
                                    </td>
                                 )}
                              </tr>
                           ))}
                        </tbody>
                     </Table>
                  </TableWrapper>
               )}
            </SectionBody>
         </Section>

         <NewCommissionModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            itemId={item.id}
            onCommissionCreated={handleCommissionCreated}
         />

         <CommissionSuggestionModal
            isOpen={isSuggestionOpen}
            onClose={() => setIsSuggestionOpen(false)}
            itemId={item.id}
            onConfirmed={fetchEntries}
         />
      </>
   );
}

ItemCommissionsPanel.propTypes = {
   item: PropTypes.object,
   canCreate: PropTypes.bool,
   canRemove: PropTypes.bool,
   onEntriesChange: PropTypes.func,
};
